import { env } from './env';

export type HighAdvocacyUser = {
  userId: string;
  email?: string;
  name?: string;
};

type HighAdvocacyConfig = {
  campaignId: string;
  darkMode: boolean;
  user: HighAdvocacyUser;
};

/** The vendor script reads the config off `window.HighAdvocacy` and adds `open()` to it once loaded. */
type HighAdvocacyGlobal = HighAdvocacyConfig & { open?: () => void };

declare global {
  interface Window {
    HighAdvocacy?: HighAdvocacyGlobal;
  }
}

const SCRIPT_ID = 'highadvocacy-widget';

let loading: Promise<void> | null = null;

/** False when either env var is unset — the prompt then never asks the API or loads the script. */
export function isHighAdvocacyConfigured(): boolean {
  return Boolean(env.highAdvocacyWidgetUrl && env.highAdvocacyCampaignId);
}

/**
 * Loads the HighAdvocacy popup widget once per page load. The config is set on
 * `window.HighAdvocacy` BEFORE the script is injected, as the vendor snippet
 * requires. Resolves when the script has loaded, rejects if it fails (e.g. an
 * ad-blocker) — a failed load is retried on the next call.
 */
export function loadHighAdvocacyPopup(
  config: Omit<HighAdvocacyConfig, 'campaignId'>,
): Promise<void> {
  if (loading) return loading;

  window.HighAdvocacy = { ...config, campaignId: env.highAdvocacyCampaignId };

  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = env.highAdvocacyWidgetUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      loading = null;
      reject(new Error('Failed to load the HighAdvocacy widget script'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Whether the loaded widget exposes `open()`. Checked before an impression is counted. */
export function canOpenHighAdvocacyPopup(): boolean {
  return typeof window.HighAdvocacy?.open === 'function';
}

export function openHighAdvocacyPopup(): void {
  window.HighAdvocacy?.open?.();
}

/**
 * Logout teardown (`clearTransientStores`). The global config names the
 * signed-in user and the script reads it once, so a client-side logout →
 * login would otherwise prompt the next account as the previous one. Drops
 * the config and the script tag; the next load injects a fresh copy.
 */
export function teardownHighAdvocacy(): void {
  document.getElementById(SCRIPT_ID)?.remove();
  delete window.HighAdvocacy;
  loading = null;
}
